import { Link } from 'react-router-dom';
import { daysLeftLabel, formatUtcDate } from './formatDate';
import { useCurrentSubscription } from './useCurrentSubscription';

/** EPN-79 — the approved design direction's persistent trial indicator ("N Days Left" + Upgrade Now), rendered
 * in `AppShell`'s topbar on every authenticated screen. Driven entirely by the server's `effectiveState`:
 *  - active trial → "Upgrade Now · N Days Left"
 *  - expired trial → "Trial Ended · View Plans" (never "0 Days Left")
 *  - payment failed, access continuing (grace) → "Payment Failed · Update Payment"; payment failed and read-only →
 *    "Payment Failed · Read-Only" (EPN-105; both link to billing, where the payment method is updated)
 *  - not started, no subscription, paid, canceled → nothing here (the site-wide banner covers them; a countdown is
 *    never fabricated)
 * Extracted from `AppShell.tsx` so it's testable without dragging in the rest of the shell's dependency
 * graph (SideNav/StagingBanner/ProfileMenu/BusinessViewSwitcher). */
export function TrialUpgradeBadge() {
  const { subscription } = useCurrentSubscription();

  if (subscription?.effectiveState === 'TrialActive') {
    return (
      <Link to="/settings/subscription" className="trial-upgrade-badge" title={`Trial ends ${formatUtcDate(subscription.trialEndUtc)}`}>
        <span aria-hidden="true">👑</span> Upgrade Now
        {daysLeftLabel(subscription.trialDaysRemaining) && (
          <span className="trial-upgrade-badge-days">{daysLeftLabel(subscription.trialDaysRemaining)}</span>
        )}
      </Link>
    );
  }

  if (subscription?.effectiveState === 'TrialExpired') {
    return (
      <Link to="/settings/subscription" className="trial-upgrade-badge trial-upgrade-badge--expired">
        <span aria-hidden="true">⏱</span> Trial Ended
        <span className="trial-upgrade-badge-days">View Plans</span>
      </Link>
    );
  }

  if (subscription?.effectiveState === 'PastDueGrace') {
    return (
      <Link to="/settings/billing" className="trial-upgrade-badge trial-upgrade-badge--expired">
        <span aria-hidden="true">⚠</span> Payment Failed
        <span className="trial-upgrade-badge-days">Update Payment</span>
      </Link>
    );
  }

  if (subscription?.effectiveState === 'PastDueRestricted') {
    return (
      <Link to="/settings/billing" className="trial-upgrade-badge trial-upgrade-badge--expired">
        <span aria-hidden="true">⛔</span> Payment Failed
        <span className="trial-upgrade-badge-days">Read-Only</span>
      </Link>
    );
  }

  return null;
}
